import { rufe } from './client';

/**
 * Der Zugang des Check-in-Tablets (TAG-49).
 *
 * Kennung und Schluessel stehen im localStorage DIESES Geraets — eingegeben
 * einmal beim Einrichten durch die Verwaltung. Es ist ein Geraeteschluessel und
 * kein Sitzungsschluessel: Er soll einen Neustart ueberdauern, denn am Tablet
 * meldet sich morgens niemand an.
 */
const ABLAGE = 'crm.checkin.geraet';

export const geraeteablage = {
  lesen(): string | null {
    try {
      return localStorage.getItem(ABLAGE);
    } catch {
      return null;
    }
  },
  schreiben(wert: string): void {
    try {
      localStorage.setItem(ABLAGE, wert);
    } catch {
      /* Ohne Ablage fragt das Tablet nach jedem Neuladen neu — laestig, aber richtig. */
    }
  },
  loeschen(): void {
    try {
      localStorage.removeItem(ABLAGE);
    } catch {
      /* nichts */
    }
  },
};

export type Checkingeraet = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly standort: string;
};

export type Suchergebnis =
  | { readonly art: 'gefunden'; readonly sitzung: string }
  | {
      readonly art: 'mehrdeutig';
      readonly frage: 'termin';
      readonly termine: readonly { readonly termin: string; readonly produkt: string; readonly beginn: string }[];
    }
  | { readonly art: 'mehrdeutig'; readonly frage: 'buchungsnummer' }
  | { readonly art: 'schon_eingecheckt' }
  /* EB-DP-06, TAG-50, TAG-51: einwortiger Name oder derselbe Name zweimal in einer Buchung. */
  | { readonly art: 'an_empfang' }
  | { readonly art: 'nicht_gefunden' };

export type Checkintext = {
  readonly id: string;
  readonly art: string;
  readonly artbezeichnung: string;
  readonly kennung: string;
  readonly version: number;
  readonly bezeichnung: string;
  readonly inhaltHtml: string;
  readonly platzhalter: boolean;
};

export type Checkinfeld = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly pflicht: boolean;
  readonly auswahl: readonly string[] | null;
  readonly reihenfolge: number;
};

export type Sitzungsinhalt = {
  readonly sitzung: string;
  readonly name: string;
  readonly produkt: string;
  readonly beginn: string;
  readonly texte: readonly Checkintext[];
  readonly felder: readonly Checkinfeld[];
};

export type Zuordnung =
  | {
      readonly art: 'zuordnung';
      readonly produkt: string;
      readonly beginn: string;
      readonly treffpunkt: string | null;
      readonly trainer: readonly string[];
      readonly stand: string;
    }
  | { readonly art: 'empfang'; readonly grund: string };

export type Abschluss = {
  readonly id: string;
  readonly texte: readonly string[];
  readonly unterschrift: string | null;
  readonly angaben: Readonly<Record<string, string>>;
  readonly abgeschlossenAm: string;
};

const mitGeraet = (geraet: string) => ({ ohneSchluessel: true, geraet }) as const;

export async function geraetPruefen(geraet: string): Promise<Checkingeraet> {
  return rufe('/checkin/geraet', mitGeraet(geraet));
}

export async function suchen(
  geraet: string,
  eingabe: { readonly name: string; readonly termin?: string; readonly buchungsnummer?: string },
): Promise<Suchergebnis> {
  return rufe('/checkin/suche', { ...mitGeraet(geraet), verfahren: 'POST', rumpf: eingabe });
}

export async function sitzung(geraet: string, id: string): Promise<Sitzungsinhalt> {
  return rufe(`/checkin/sitzungen/${encodeURIComponent(id)}`, mitGeraet(geraet));
}

export async function abschliessen(geraet: string, id: string, abschluss: Abschluss): Promise<Zuordnung> {
  return rufe(`/checkin/sitzungen/${encodeURIComponent(id)}/abschluss`, {
    ...mitGeraet(geraet),
    verfahren: 'POST',
    rumpf: abschluss,
  });
}

// ---------------------------------------------------------------------------
// Das Haus: Geraete, Zusatzangaben, Empfang (TAG-55, TAG-62, TAG-65)
// ---------------------------------------------------------------------------

export type Geraetezeile = Checkingeraet & {
  readonly aktiv: boolean;
  readonly zuletztGesehenAm: string | null;
  readonly angelegtAm: string;
};

export async function geraete(): Promise<readonly Geraetezeile[]> {
  return (await rufe<{ readonly geraete: readonly Geraetezeile[] }>('/checkin-geraete')).geraete;
}

export async function geraetAnlegen(eingabe: {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly standort: string;
}): Promise<{ readonly id: string; readonly schluessel: string }> {
  return rufe('/checkin-geraete', { verfahren: 'POST', rumpf: eingabe });
}

export async function geraetSperren(id: string): Promise<void> {
  await rufe(`/checkin-geraete/${encodeURIComponent(id)}/sperren`, { verfahren: 'POST' });
}

export async function felder(artikel: string): Promise<readonly Checkinfeld[]> {
  return (await rufe<{ readonly felder: readonly Checkinfeld[] }>(`/checkin-felder/${encodeURIComponent(artikel)}`)).felder;
}

export async function felderSetzen(artikel: string, liste: readonly Checkinfeld[]): Promise<readonly Checkinfeld[]> {
  return (
    await rufe<{ readonly felder: readonly Checkinfeld[] }>(`/checkin-felder/${encodeURIComponent(artikel)}`, {
      verfahren: 'PUT',
      rumpf: { felder: liste },
    })
  ).felder;
}

export type Checkinstand = {
  readonly checkin: string;
  readonly teilnehmer: string;
  readonly art: 'selbst' | 'fremd';
  readonly abgeschlossenAm: string;
  readonly erfasstVon: string | null;
};

export async function staende(termine: readonly string[]): Promise<readonly Checkinstand[]> {
  if (termine.length === 0) return [];
  const q = new URLSearchParams({ termine: termine.join(',') });
  return (await rufe<{ readonly checkins: readonly Checkinstand[] }>(`/empfang/checkins?${q.toString()}`)).checkins;
}

export async function stellvertretend(teilnehmer: string, abschluss: Abschluss): Promise<Zuordnung> {
  return rufe(`/empfang/checkin/${encodeURIComponent(teilnehmer)}`, { verfahren: 'POST', rumpf: abschluss });
}

export async function korrigieren(checkin: string, grund: string): Promise<void> {
  await rufe(`/empfang/checkin/${encodeURIComponent(checkin)}/korrektur`, {
    verfahren: 'POST',
    rumpf: { grund },
  });
}

/** Eine neue Kennung fuer den Abschluss — vom Geraet vergeben, damit eine Wiederholung einer bleibt. */
export function neueKennung(): string {
  return crypto.randomUUID();
}

export async function vorlage(
  teilnehmer: string,
): Promise<{ readonly texte: readonly Checkintext[]; readonly felder: readonly Checkinfeld[] }> {
  return rufe(`/empfang/checkin/${encodeURIComponent(teilnehmer)}/vorlage`);
}
